import React, { useEffect, useState } from 'react';

import { CLS } from '@/components/common/LCPrimitives';
import apiService from '@/services/apiService';
import { ApiEffectiveMarginResponse } from '@/types/effectiveMargin';

import EffectiveMarginAllocationAudit from './EffectiveMarginAllocationAudit';
import EffectiveMarginStrategyBreakdown from './EffectiveMarginStrategyBreakdown';
import EffectiveMarginSummary from './EffectiveMarginSummary';
import { formatSourceTime } from './formatters';


interface EffectiveMarginApi {
  getEffectiveMargin(portfolioId: string): Promise<ApiEffectiveMarginResponse>;
}

interface EffectiveMarginBreakdownProps {
  portfolioId: string;
  api?: EffectiveMarginApi;
  onViewPositions?: () => void;
}

const EffectiveMarginBreakdown: React.FC<EffectiveMarginBreakdownProps> = ({
  portfolioId,
  api = apiService,
  onViewPositions,
}) => {
  const [response, setResponse] = useState<ApiEffectiveMarginResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    setResponse(null);

    api.getEffectiveMargin(portfolioId)
      .then(result => {
        if (active) setResponse(result);
      })
      .catch(reason => {
        if (active) setError(reason instanceof Error ? reason.message : 'Effective Margin Breakdown unavailable');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [api, portfolioId]);

  return (
    <section aria-label="Effective Margin Analysis">
      <div role="region" aria-label="Primary effective margin analysis" className="border border-black/10 bg-white p-4">
        <div className="mb-4 flex items-center justify-between gap-4 border border-black/10 bg-[#F7F7F6] px-4 py-3 text-[11px] text-[#555] max-[700px]:items-start max-[700px]:flex-col">
          <span><strong className="text-[#222]">Effective Margin</strong> uses available broker position data. Some rows or values may differ from Portfolio Positions.</span>
          {onViewPositions && <button type="button" onClick={onViewPositions} className="border-0 bg-transparent p-0 text-[10px] font-bold uppercase tracking-[0.08em] text-[#B00020] hover:underline">View source positions →</button>}
        </div>

        {loading && (
          <div className={`${CLS.surface} px-5 py-7 text-[12px] text-[#666]`}>Loading Effective Margin analysis…</div>
        )}

        {!loading && (error || response?.status === 'SNAPSHOT_UNAVAILABLE') && (
          <div role="alert" className="border border-[#b6770b]/35 bg-[#b6770b]/[0.07] px-5 py-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.13em] text-[#8a5700]">Analysis unavailable</div>
            <div className="mt-1.5 text-[13px] font-medium">{error || response?.source_status_message || 'Effective Margin Breakdown unavailable'}</div>
            <div className="mt-1 text-[11.5px] text-[#666]">Portfolio Positions remain available. No zero requirement has been inferred.</div>
            <div className="mt-1 text-[10px] text-[#666]">{response?.retrieved_at ? `Retrieved ${formatSourceTime(response.retrieved_at, 'Retrieval time unavailable')}` : 'Retrieval time unavailable'}</div>
          </div>
        )}

        {!loading && response?.status === 'AVAILABLE' && response.totals && (
          <>
            <div className="mb-3 text-right text-[10px] text-[#666]">{response.retrieved_at ? `Retrieved ${formatSourceTime(response.retrieved_at, 'Retrieval time unavailable')}` : 'Retrieval time unavailable'}</div>
            {response.position_results.some(result => result.result_state === 'REQUIREMENT_UNAVAILABLE') && (
              <div role="status" className="mb-3 border-l-2 border-[#b6770b] bg-[#b6770b]/[0.07] px-4 py-3 text-[12px]">
                One or more positions have no available Pershing requirement. Their Effective Margin and reduction remain unavailable rather than being shown as zero.
              </div>
            )}
            <EffectiveMarginSummary totals={response.totals} />
            <EffectiveMarginStrategyBreakdown results={response.position_results} totals={response.totals} />
          </>
        )}
      </div>
      {!loading && response?.status === 'AVAILABLE' && response.totals && (
        <EffectiveMarginAllocationAudit results={response.position_results} totals={response.totals} allocationState={response.allocation_state} reconciliationState={response.reconciliation_state} checks={response} />
      )}
    </section>
  );
};

export default EffectiveMarginBreakdown;
